import { useEffect, useRef, useState } from "react";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SubtaskId, Task, TaskId } from "../../../../shared/types";
import { CATEGORY_BAR_COLORS } from "../../../../shared/types";
import { TaskItemMenus } from "./TaskItemMenus";
import { TaskItemRow } from "./TaskItemRow";
import { useTaskItemState } from "../../hooks/useTaskItemState";
import { onHighlightTask } from "../../../../lib/navigation-bus";

interface TaskItemProps {
  readonly task: Task;
  readonly showCategory?: boolean;
  readonly selectable?: boolean;
  readonly selected?: boolean;
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onUpdate: (
    id: TaskId,
    updates: Partial<
      Pick<Task, "title" | "priority" | "notes" | "tags" | "dueDate">
    >,
  ) => void;
  readonly onDelete: (id: TaskId) => void;
  readonly onAddSubtask: (taskId: TaskId, title: string) => void;
  readonly onToggleSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onDeleteSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onSelect?: (id: TaskId) => void;
  readonly dragHandleProps?: {
    readonly attributes: DraggableAttributes;
    readonly listeners: Record<string, unknown> | undefined;
  };
}

export function TaskItem({
  task,
  showCategory = false,
  selectable = false,
  selected = false,
  onCycleStatus,
  onUpdate,
  onDelete,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
  onSelect,
  dragHandleProps,
}: TaskItemProps) {
  const state = useTaskItemState(task, onUpdate);
  const containerRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    return onHighlightTask(({ taskId }) => {
      if (taskId !== task.id) return;
      setHighlighted(true);
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      setTimeout(() => setHighlighted(false), 2000);
    });
  }, [task.id]);

  return (
    <div
      ref={containerRef}
      className={`relative border-r border-border/40 transition-all duration-300 ${
        highlighted
          ? "bg-accent/5 ring-1 ring-accent/30"
          : "hover:bg-surface-alt/30"
      }`}
    >
      <div
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
        style={{ touchAction: "none" }}
        className={`absolute left-0 top-0 bottom-0 z-20 ${CATEGORY_BAR_COLORS[task.category]} ${dragHandleProps ? "w-1 cursor-grab active:cursor-grabbing" : "w-0.5"}`}
      />
      {state.swipe.swiping && <SwipeBackground swipeX={state.swipe.swipeX} />}
      <TaskItemRow
        task={task}
        swipe={state.swipe}
        longPress={state.longPress}
        selectable={selectable}
        selected={selected}
        isEditing={state.isEditing}
        editValue={state.editValue}
        isDone={state.isDone}
        isExpanded={state.isExpanded}
        inputRef={state.inputRef}
        onCycleStatus={onCycleStatus}
        onEditValueChange={state.setEditValue}
        onStartEdit={state.startEdit}
        onCommitEdit={state.commitEdit}
        onKeyDown={state.handleKeyDown}
        onSelect={onSelect}
        onToggleExpand={() => state.setIsExpanded(!state.isExpanded)}
        onDelete={() => state.setConfirmDelete(true)}
        showCategory={showCategory}
        onUpdate={onUpdate}
        onAddSubtask={onAddSubtask}
        onToggleSubtask={onToggleSubtask}
        onDeleteSubtask={onDeleteSubtask}
      />

      <TaskItemMenus
        taskId={task.id}
        taskTitle={task.title}
        taskPriority={task.priority}
        contextMenu={state.contextMenu}
        confirmDelete={state.confirmDelete}
        onContextMenuClose={() => state.setContextMenu(null)}
        onStartEdit={state.startEdit}
        onTogglePriority={(priority) => onUpdate(task.id, { priority })}
        onConfirmDelete={() => {
          state.setConfirmDelete(false);
          onDelete(task.id);
        }}
        onCancelDelete={() => state.setConfirmDelete(false)}
      />
    </div>
  );
}

interface SwipeBackgroundProps {
  readonly swipeX: number;
}

function SwipeBackground({ swipeX }: SwipeBackgroundProps) {
  return (
    <div
      className={`absolute inset-0 z-0 flex items-center ${
        swipeX < 0
          ? "justify-end bg-danger/20"
          : "justify-start bg-green-500/20"
      }`}
    >
      <span className="px-4 font-semibold text-xs text-ink-faint">
        {swipeX < 0 ? "Supprimer" : "Changer statut"}
      </span>
    </div>
  );
}
